import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
} from 'react';

export type SetDrawerFooter = Dispatch<SetStateAction<ReactNode>>;

/**
 * Shared footer registration channel for the V2 drawer family.
 *
 * Both `Drawer` and `BottomDrawer` render this provider, so `useDrawerFooter`
 * behaves identically inside either one. Kept in its own module (instead of
 * inside `Drawer.tsx`) so the two components can share a single context
 * instance — two `createContext` calls would produce two unrelated contexts.
 */
export const DrawerFooterContext = createContext<SetDrawerFooter | null>(null);

/**
 * Registers footer content with the nearest V2 Drawer (side panel) or
 * BottomDrawer (mobile sheet).
 * When called inside one of them, the footer renders in that drawer's pinned
 * footer slot. Returns true if inside a drawer, false otherwise (so the
 * caller can render a fallback inline).
 */
export function useDrawerFooter(footer: ReactNode): boolean {
  const setFooter = useContext(DrawerFooterContext);

  useEffect(() => {
    if (!setFooter) return undefined;
    setFooter(footer);
    return () => setFooter(null);
  }, [setFooter, footer]);

  return setFooter !== null;
}
